import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { NavigationError, provideRouter, withNavigationErrorHandler, withViewTransitions } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

import { routes } from './app.routes';
import { errorInterceptor } from '../core/interceptors/error.interceptor';
import { jwtInterceptor } from '../core/interceptors/jwt.interceptor';
import { systemInterceptor } from '../core/interceptors/system.interceptor';
import { encryptionInterceptor } from '../shared/crypto/encryption.interceptor';

// ── Tema de PrimeNG: debe seguir la escala `primary` de styles.css (@theme) ─────────────
const IsbenPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50:  '#EBF2FF',
      100: '#CCE0FF',
      200: '#99C2FF',
      300: '#66A3FF',
      400: '#3384FF',
      500: '#0058FC',  // = --color-primary-500 de styles.css: si cambia la marca, cambiar ambos
      600: '#0048CE',
      700: '#0037A0',
      800: '#002772',
      900: '#001744',
      950: '#000E2B',
    },
    colorScheme: {
      light: {
        primary: {
          color:           '{primary.500}',
          inverseColor:    '#ffffff',
          hoverColor:      '{primary.600}',
          activeColor:     '{primary.700}',
        },
        highlight: {
          background:      '{primary.500}',
          focusBackground: '{primary.600}',
          color:           '#ffffff',
          focusColor:      '#ffffff',
        },
      },
      dark: {
        primary: {
          color:           '{primary.500}',
          inverseColor:    '#ffffff',
          hoverColor:      '{primary.600}',
          activeColor:     '{primary.700}',
        },
        highlight: {
          background:      'rgba(0,88,252,0.16)',
          focusBackground: 'rgba(0,88,252,0.24)',
          color:           '{primary.500}',
          focusColor:      '{primary.500}',
        },
      },
    },
  },
});

/** Tras un deploy, los chunks del build anterior ya no existen: se recarga una vez para traer el index nuevo. */
const reloadOnStaleChunk = (e: NavigationError): void => {
  if (!/dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(String((e.error as any)?.message ?? e.error))) return;
  try {
    const last = Number(sessionStorage.getItem('chunk-reload') || 0);
    if (Date.now() - last < 10_000) return; // si el servidor sigue sirviendo mal, no entra en bucle
    sessionStorage.setItem('chunk-reload', String(Date.now()));
  } catch { /* sin sessionStorage: se recarga igual una vez por error */ }
  window.location.assign(e.url);
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withViewTransitions(), withNavigationErrorHandler(reloadOnStaleChunk)),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([systemInterceptor, jwtInterceptor, errorInterceptor, encryptionInterceptor])),
    providePrimeNG({
      theme: {
        preset: IsbenPreset,
        options: {
          darkModeSelector: 'none', // Un solo tema fijo — sin toggle dark/light
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
  ],
};
